import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faMoon,
  faSun,
  faTriangleExclamation,
  faBrain,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import useGrowthLoop from "./hooks/useGrowthLoop";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

// Sample/demo data — kept exactly as before, shown only when "Show Sample
// Data" is toggled on. Real data (below) replaces it when toggled off.
const dummyChildren = [
  {
    id: 1,
    name: "Guddu",
    age: 8,
    riskBand: "Amber",
    growth: [120, 121, 122, 123],
    emotion: [6, 7, 5, 8],
    months: ["Jan", "Feb", "Mar", "Apr"],
  },
  {
    id: 2,
    name: "Sonika",
    age: 6,
    riskBand: "Green",
    streak: 9,
    points: 210,
    growth: [110, 111, 112, 113],
    emotion: [8, 7, 9, 8],
    months: ["Jan", "Feb", "Mar", "Apr"],
  },
];

// 6 domains tracked (matches GrowthLoopSeed.js DOMAINS ids exactly) —
// duplicated here as a plain id list since Dashboard doesn't need the full
// question content, just the ids to walk each assessment's `responses` map.
const DOMAIN_IDS = ["screen", "executive", "emotional", "esteem", "learning", "adhd"];
const ROLES = ["parent", "educator", "doctor", "observer"];
const MAX_PER_ITEM = 3; // SCALE = [0,1,2,3], matches GrowthLoopHome.jsx

// Severity % for one domain within one assessment, averaged across every
// role that has data for it. Higher % = more concern (confirmed against
// GrowthLoopHome.jsx's shouldDeepDive, which triggers at severity >= 0.6 —
// same direction used here for the risk band).
function domainSeverity(assessmentResponses, domainId) {
  let total = 0;
  let max = 0;

  ROLES.forEach((role) => {
    const answers = assessmentResponses?.[role]?.[domainId];
    if (!answers) return;
    const values = Object.values(answers);
    total += values.reduce((sum, v) => sum + (Number(v) || 0), 0);
    max += values.length * MAX_PER_ITEM;
  });

  return max ? total / max : null;
}

// Overall severity % across all 6 domains for one assessment.
function overallSeverity(assessmentResponses) {
  const perDomain = DOMAIN_IDS
    .map((id) => domainSeverity(assessmentResponses, id))
    .filter((v) => v !== null);

  if (!perDomain.length) return null;
  return perDomain.reduce((a, b) => a + b, 0) / perDomain.length;
}

function riskBandFromSeverity(severity) {
  if (severity === null) return "—";
  if (severity >= 0.75) return "Red";
  if (severity >= 0.6) return "Amber";
  return "Green";
}

const ROLE_LABEL = { parent: "Parent", educator: "Educator", doctor: "Doctor", observer: "Observer" };

export default function GrowthLoopDashboard() {
  const { loadMyChildren, loadMyAssessments } = useGrowthLoop();

  const [darkMode, setDarkMode] = useState(true);
  const [showSample, setShowSample] = useState(false);

  const [activeChildId, setActiveChildId] = useState(dummyChildren[0].id);
  const [symptomInput, setSymptomInput] = useState("");

  const [loadingReal, setLoadingReal] = useState(true);
  const [realChild, setRealChild] = useState(null);
  const [realAssessments, setRealAssessments] = useState([]);

  useEffect(() => {
    (async () => {
      setLoadingReal(true);

      const childRes = await loadMyChildren();
      if (childRes?.success && childRes.data?.length) {
        setRealChild(childRes.data[0]); // one child per account
      }

      const assessRes = await loadMyAssessments();
      if (assessRes?.success && assessRes.data?.length) {
        const sorted = [...assessRes.data].sort(
          (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
        );
        setRealAssessments(sorted);
      }

      setLoadingReal(false);
    })();
    // eslint-disable-next-line
  }, []);

  const activeChild = dummyChildren.find((c) => c.id === activeChildId);

  const growthData = {
    labels: activeChild.months,
    datasets: [
      {
        label: "Height (cm)",
        data: activeChild.growth,
        borderColor: "#10b981",
        backgroundColor: "rgba(16,185,129,0.2)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const emotionData = {
    labels: activeChild.months,
    datasets: [
      {
        label: "Emotion Score",
        data: activeChild.emotion,
        borderColor: "#6366f1",
        backgroundColor: "rgba(99,102,241,0.2)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    scales: {
      x: { ticks: { color: "#fff" } },
      y: { ticks: { color: "#fff" } },
    },
    plugins: { legend: { labels: { color: "#fff" } } },
  };

  // --- Real data derivations ---
  const realLabels = realAssessments.map((a) =>
    new Date(a.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  );

  const realOverallSeries = realAssessments.map((a) => {
    const s = overallSeverity(a.responses);
    return s === null ? null : Math.round(s * 100);
  });

  const realEmotionSeries = realAssessments.map((a) => {
    const s = domainSeverity(a.responses, "emotional");
    return s === null ? null : Math.round(s * 100);
  });

  const realOverallData = {
    labels: realLabels,
    datasets: [
      {
        label: "Overall Pattern Severity (%)",
        data: realOverallSeries,
        borderColor: "#10b981",
        backgroundColor: "rgba(16,185,129,0.2)",
        tension: 0.4,
        fill: true,
        spanGaps: true,
      },
    ],
  };

  const realEmotionData = {
    labels: realLabels,
    datasets: [
      {
        label: "Emotional Domain Severity (%)",
        data: realEmotionSeries,
        borderColor: "#6366f1",
        backgroundColor: "rgba(99,102,241,0.2)",
        tension: 0.4,
        fill: true,
        spanGaps: true,
      },
    ],
  };

  const latestAssessment = realAssessments[realAssessments.length - 1] || null;
  const latestSeverity = latestAssessment ? overallSeverity(latestAssessment.responses) : null;
  const realRiskBand = riskBandFromSeverity(latestSeverity);

  // Live feed derived from real assessment data — which roles actually
  // submitted, and when. No new collection; reuses gl_assessment as-is.
  const realFeed = [...realAssessments]
    .reverse()
    .slice(0, 5)
    .flatMap((a) =>
      ROLES.filter((r) => a.responses?.[r] && Object.keys(a.responses[r]).length > 0).map(
        (r) => ({
          text: `${ROLE_LABEL[r]} submitted an assessment`,
          date: new Date(a.updatedAt).toLocaleDateString(),
        }),
      ),
    )
    .slice(0, 5);

  return (
    <div
      className={`${darkMode ? "dark bg-black text-white [&_p]:text-white [&_h2]:text-yellow-200" : "bg-gray-700 text-white"} min-h-screen transition-colors duration-300`}
    >
      <div className="max-w-7xl mx-auto p-2 space-y-2 border-2 rounded-xl">
        <div className="flex justify-between items-center">
          <h1 className="text-sm lg:text-lg font-bold tracking-tight">
            GrowthLoop: Think5Whys Child Transformation Dashboard
          </h1>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="w-fit px-2 py-1 rounded-xl border border-gray-300 dark:border-gray-700 hover:scale-105 transition"
          >
            <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
          </button>
        </div>

        <div>
          <button
            onClick={() => setShowSample((s) => !s)}
            className="w-full rounded-lg py-1 px-2 mb-3 text-xs font-semibold bg-gray-800 border border-gray-700 text-cyan-300 hover:bg-gray-700"
          >
            {showSample ? "Hide Sample Data (show real data)" : "Show Sample Data"}
          </button>

          {showSample ? (
            <>
              <div className="flex gapx-2 py-1 overflow-x-auto">
                {dummyChildren.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => setActiveChildId(child.id)}
                    className={`w-fit px-2 py-1 rounded-xl flex items-center whitespace-nowrap transition
                ${
                  activeChildId === child.id
                    ? "bg-emerald-600 text-black"
                    : "bg-gray-800 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700"
                }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    {child.name}
                  </button>
                ))}
              </div>
              {activeChild.riskBand !== "Green" && (
                <div className="bg-yellow-100 dark:bg-yellow-900 border border-yellow-500 rounded-xl p-2 flex gap-1 items-center">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <div>
                    <p className="font-semibold">Preventive Risk Alert</p>
                    <p className="text-sm opacity-80">
                      Pattern trending toward {activeChild.riskBand}. Expert review advised.
                    </p>
                  </div>
                </div>
              )}
              <div className="bg-black dark:bg-gray-900 rounded-xl shadow-xl px-2 py-1 space-y-2">
                <h2 className="text-sm lg:text-lg text-white font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faBrain} />5 Whys Insight and Think5Whys Causal OS / Engine
                </h2>
                <div className="flex flex-row gap-1">
                  <input
                    value={symptomInput}
                    onChange={(e) => setSymptomInput(e.target.value)}
                    placeholder="Enter concern..."
                    className="w-1/2 px-2 py-1 rounded-xl text-white bg-cyan-700 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    disabled
                    className="w-fit px-2 py-1 rounded-xl border border-gray-300 dark:border-gray-700 hover:scale-105 transition"
                  >
                    <FontAwesomeIcon icon={faBrain} /> RCA
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-black text-white dark:bg-gray-900 rounded-xl shadow-xl p-2">
                  <h3 className="font-semibold mb-4 text-cyan-200">Growth Curve</h3>
                  <Line data={growthData} options={chartOptions} />
                </div>
                <div className="bg-black text-white dark:bg-gray-900 rounded-xl shadow-xl p-2">
                  <h3 className="font-semibold mb-4 text-cyan-200">Emotional Trend</h3>
                  <Line data={emotionData} options={chartOptions} />
                </div>
              </div>
              <div className="bg-black text-white dark:bg-gray-900 rounded-xl shadow-xl px-2 py-1 space-y-2">
                <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold flex items-center gap-2">
                  <FontAwesomeIcon icon={faComments} />
                  Live Collaboration Feed
                </h2>
                <div className="space-y-3 text-sm">
                  <div className="px-2 py-1 rounded-xl bg-gray-600 text-white dark:bg-gray-800">
                    Pediatrician updated growth percentile.
                  </div>
                  <div className="px-2 py-1 rounded-xl bg-gray-600 text-white dark:bg-gray-800">
                    Psychologist added emotional exercise.
                  </div>
                  <div className="px-2 py-1 rounded-xl bg-gray-600 text-white dark:bg-gray-800">
                    Parent completed reflection.
                  </div>
                </div>
              </div>
            </>
          ) : loadingReal ? (
            <div className="text-xs text-gray-400 text-center py-8">Loading your dashboard...</div>
          ) : !realChild ? (
            <div className="bg-yellow-900/20 border border-yellow-600 rounded-xl p-4 text-center text-yellow-300 text-sm">
              No child profile yet — create one under Child Profile to see your dashboard.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-2 py-1">
                <FontAwesomeIcon icon={faUser} />
                <span className="font-semibold">{realChild.name}</span>
                {realChild.age && <span className="text-xs text-gray-400">Age {realChild.age}</span>}
              </div>

              {!realAssessments.length ? (
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 text-center text-sm text-gray-300">
                  No assessments logged yet — complete a Parent, Educator, Doctor or Observer
                  assessment to see trends here.
                </div>
              ) : (
                <>
                  {realRiskBand !== "Green" && realRiskBand !== "—" && (
                    <div className="bg-yellow-100 dark:bg-yellow-900 border border-yellow-500 rounded-xl p-2 flex gap-1 items-center">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      <div>
                        <p className="font-semibold">Preventive Risk Alert</p>
                        <p className="text-sm opacity-80">
                          Latest pattern trending toward {realRiskBand}. Expert review advised.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="bg-black dark:bg-gray-900 rounded-xl shadow-xl px-2 py-1 space-y-2">
                    <h2 className="text-sm lg:text-lg text-white font-semibold flex items-center gap-2">
                      <FontAwesomeIcon icon={faBrain} />5 Whys Insight and Think5Whys Causal OS / Engine
                    </h2>
                    <div className="flex flex-row gap-1">
                      <input
                        value={symptomInput}
                        onChange={(e) => setSymptomInput(e.target.value)}
                        placeholder="Enter concern..."
                        className="w-1/2 px-2 py-1 rounded-xl text-white bg-cyan-700 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <button
                        disabled
                        className="w-fit px-2 py-1 rounded-xl border border-gray-300 dark:border-gray-700 hover:scale-105 transition"
                      >
                        <FontAwesomeIcon icon={faBrain} /> RCA
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-black text-white dark:bg-gray-900 rounded-xl shadow-xl p-2">
                      <h3 className="font-semibold mb-4 text-cyan-200">Overall Pattern Trend</h3>
                      <Line data={realOverallData} options={chartOptions} />
                    </div>
                    <div className="bg-black text-white dark:bg-gray-900 rounded-xl shadow-xl p-2">
                      <h3 className="font-semibold mb-4 text-cyan-200">Emotional Trend</h3>
                      <Line data={realEmotionData} options={chartOptions} />
                    </div>
                  </div>

                  <div className="bg-black text-white dark:bg-gray-900 rounded-xl shadow-xl px-2 py-1 space-y-2">
                    <h2 className="text-sm lg:text-lg text-cyan-200 font-semibold flex items-center gap-2">
                      <FontAwesomeIcon icon={faComments} />
                      Live Collaboration Feed
                    </h2>
                    <div className="space-y-3 text-sm">
                      {realFeed.length ? (
                        realFeed.map((item, i) => (
                          <div
                            key={i}
                            className="px-2 py-1 rounded-xl bg-gray-600 text-white dark:bg-gray-800 flex justify-between"
                          >
                            <span>{item.text}</span>
                            <span className="text-gray-300 text-xs">{item.date}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-400">No activity yet.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
